import { ChevronsDown, FileText } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { ErrorState, Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCallRecording, useCallTranscript, useTranscribeCall } from "@/lib/queries";
import { followScrollTop } from "@/lib/recording-playback";
import {
  SPEAKER_HINTS, SPEAKER_LABELS, activeAt, transcribeAction, transcriptIsBlank, transcriptNotice,
  transcriptStamp,
} from "@/lib/transcripts";
import type { TranscriptSegment } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The words of one call, in the rows of the block under its transport (`CallAudio`).
 *
 * **What it renders.** Rows of the block it sits in, not a box of its own: a first row carrying
 * the file's facts (`meta`, which is the block's to say) beside the transcript's own notice and
 * button, then a section per source. It returns a fragment so the block's dividers fall between
 * them, which is the one thing that couples it to its parent.
 *
 * **The two sources are never merged.** `live` was captured turn by turn during the call;
 * `recording` is a whole-file pass over the audio afterwards. They are independent passes and
 * can disagree — different segmentation, different words in the hard places — so a reader who
 * cannot tell which produced a line cannot judge it. Where both exist the recording pass leads,
 * because it heard the whole call at once and is the better transcript.
 *
 * **Only recording lines seek, and they follow the playhead.** A recording line carries
 * `start_seconds` from the same file the transport plays, so clicking it puts the playhead there
 * (`onSeek`); `currentSeconds` flows back so the line being spoken is marked and, while
 * `following`, kept in view. Live lines are timed from when the tap started — when a rep pressed
 * Transcribe — not from the start of the file, so a seek on one would land somewhere plausible
 * and wrong; they are read-only.
 *
 * **What it asks for.** The recording row is the transport's query, shared from the cache rather
 * than fetched twice. The transcript is asked for when the call row says there is one or there
 * is a recording a pass could have been run on. What shows then — lines, a job's state, a button
 * — is `lib/transcripts.ts`'s to decide.
 */
export function CallTranscript({
  callId,
  hasTranscript,
  meta,
  onSeek,
  currentSeconds,
  following = true,
  onFollowingChange,
}: {
  callId: string | undefined;
  hasTranscript: boolean;
  /** The block's own line about the file — download, size — shown at the left of the first row. */
  meta?: React.ReactNode;
  onSeek?: (seconds: number) => void;
  currentSeconds?: number;
  /** Whether the recording section scrolls to keep the line being spoken in view. */
  following?: boolean;
  /** Told false when the reader scrolls the lines themselves, true when they ask to follow again. */
  onFollowingChange?: (following: boolean) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const recording = useCallRecording(callId);
  const hasRecording = Boolean(recording.data);
  const { data, isPending, error } = useCallTranscript(callId, hasTranscript || hasRecording);
  const transcribe = useTranscribeCall();

  const wanted = Boolean(callId) && (hasTranscript || hasRecording);
  const action = wanted ? transcribeAction(data, { role: user?.role, hasRecording }) : null;
  const notice = wanted ? transcriptNotice(data) : null;

  return (
    <>
      {(meta || notice || action) && (
        <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-1.5">
          {meta ?? <span />}
          <span className="ml-auto inline-flex items-center gap-3">
            {notice && (
              <p
                role="status"
                className={cn(
                  "inline-flex items-center gap-1.5 text-xs",
                  notice.kind === "pending" ? "text-text-subtle" : "text-text-muted",
                )}
              >
                {notice.kind === "pending" && <Spinner className="size-3.5" />}
                {notice.text}
              </p>
            )}
            {action && callId && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                loading={transcribe.isPending}
                onClick={() =>
                  transcribe.mutate(
                    { callId },
                    {
                      onError: (e) =>
                        toast.error(
                          "Could not ask for a transcript",
                          e instanceof ApiError ? e.message : "Please try again.",
                        ),
                    },
                  )
                }
              >
                <FileText aria-hidden />
                {action === "again" ? "Transcribe again" : "Transcribe"}
              </Button>
            )}
          </span>
        </div>
      )}
      {!wanted ? null : isPending ? (
        <div className="px-3 py-2">
          <Spinner className="size-4" />
        </div>
      ) : error ? (
        <div className="px-3 py-2">
          <ErrorState error={error} title="Could not load the transcript" />
        </div>
      ) : transcriptIsBlank(data, action) ? null : (
        <>
          {data && data.recording.length > 0 && (
            <Section
              title="From the recording"
              hint="A whole-file pass over the audio after the call."
              segments={data.recording}
              onSeek={onSeek}
              currentSeconds={currentSeconds}
              following={following}
              onFollowingChange={onFollowingChange}
            />
          )}
          {data && data.live.length > 0 && (
            <Section
              title="Captured live"
              hint="Transcribed turn by turn while the call was in progress."
              segments={data.live}
            />
          )}
        </>
      )}
    </>
  );
}

/**
 * One source's lines, capped and scrollable: a ten-minute call is a hundred lines, and letting
 * it run would push everything below it out of the drawer.
 *
 * **Following.** While `following`, the line being spoken is scrolled into view when it leaves
 * it (`followScrollTop` decides when and where). A scroll the reader makes themselves turns
 * following off — a list that snaps back while they are reading is worse than one that does not
 * follow at all. Theirs is told from every other scroll by the input that caused it: a wheel, a
 * touch, a pointer on the list's own scrollbar or a scrolling key, each noted just before the
 * `scroll` event it produces. The scroll this component asks for has none, and neither does the
 * browser's own — it scrolls a clicked line into view on focus, by a fraction of a pixel, which
 * a comparison of positions read as the reader's hand. A *Follow playback* control in the title
 * row turns it back on; so does any press that moves the playhead, which the block handles.
 */
function Section({
  title,
  hint,
  segments,
  onSeek,
  currentSeconds,
  following = true,
  onFollowingChange,
}: {
  title: string;
  hint: string;
  segments: TranscriptSegment[];
  onSeek?: (seconds: number) => void;
  currentSeconds?: number;
  following?: boolean;
  onFollowingChange?: (following: boolean) => void;
}) {
  const activeSeq = currentSeconds === undefined ? null : activeAt(segments, currentSeconds);
  const list = React.useRef<HTMLDivElement>(null);
  const active = React.useRef<HTMLElement | null>(null);
  // When the reader last did something that scrolls the list. A `scroll` event that follows
  // one closely is theirs; any other is the browser's or this component's.
  const readerInput = React.useRef(Number.NEGATIVE_INFINITY);
  const noteInput = () => {
    readerInput.current = performance.now();
  };

  React.useEffect(() => {
    if (!following || activeSeq === null) return;
    const el = list.current;
    const line = active.current;
    if (!el || !line) return;
    const top = followScrollTop(el, line);
    if (top !== null) el.scrollTop = top;
  }, [activeSeq, following]);

  function onScroll() {
    if (following && performance.now() - readerInput.current < READER_SCROLL_MS) {
      onFollowingChange?.(false);
    }
  }

  const canFollow = onFollowingChange && currentSeconds !== undefined;

  return (
    <section className="px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h4 className="text-2xs font-medium uppercase tracking-wide text-text-subtle" title={hint}>
          {title}
        </h4>
        {canFollow && !following && (
          <button
            type="button"
            onClick={() => onFollowingChange(true)}
            className="inline-flex items-center gap-1 rounded text-2xs text-accent-11 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-8"
          >
            <ChevronsDown className="size-3" aria-hidden />
            Follow playback
          </button>
        )}
      </div>
      <div
        ref={list}
        onScroll={onScroll}
        onWheel={noteInput}
        onTouchMove={noteInput}
        // The list itself and not a line in it: a press on the scrollbar lands on the list, a
        // press on a line is a seek, and the scroll that follows a seek is this component's.
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) noteInput();
        }}
        onKeyDown={(e) => {
          if (SCROLL_KEYS.has(e.key)) noteInput();
        }}
        className="relative max-h-72 space-y-0.5 overflow-y-auto pr-1 text-xs scrollbar-subtle"
      >
        {segments.map((segment) => (
          <Line
            key={segment.seq}
            segment={segment}
            active={segment.seq === activeSeq}
            onSeek={onSeek}
            ref={segment.seq === activeSeq ? (el) => { active.current = el; } : undefined}
          />
        ))}
      </div>
    </section>
  );
}

/** How long after a wheel, touch, scrollbar press or scrolling key a `scroll` event is still
 *  read as the reader's. Momentum keeps refreshing it; a single tick lands within a frame. */
const READER_SCROLL_MS = 500;

/** The keys that scroll a list from a focused line in it. Enter and Space press the line. */
const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"]);

function Line({
  segment,
  active,
  onSeek,
  ref,
}: {
  segment: TranscriptSegment;
  active: boolean;
  onSeek?: (seconds: number) => void;
  /** Set on the line being spoken, so the section can scroll to it. */
  ref?: (el: HTMLElement | null) => void;
}) {
  const label = SPEAKER_LABELS[segment.speaker] ?? null;

  const body = (
    <>
      <span className="mr-1.5 font-mono text-2xs tabular text-text-subtle">
        {transcriptStamp(segment.start_seconds)}
      </span>
      {label && (
        <span
          title={SPEAKER_HINTS[segment.speaker]}
          className={cn(
            "mr-1 font-medium",
            segment.speaker === "near" && "text-accent-11",
            segment.speaker === "far" && "text-success-11",
          )}
        >
          {label}:
        </span>
      )}
      <span className="text-text">{segment.text}</span>
    </>
  );

  // A real <button> when it seeks, a <p> when it does not — rather than a div with an onClick
  // either way. The keyboard and the screen reader both need to know which of the two this is,
  // and a line that does nothing must not announce itself as pressable.
  if (!onSeek) {
    return (
      <p ref={ref} className={cn("rounded px-1 py-0.5", active && "bg-accent-3")}>
        {body}
      </p>
    );
  }
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onSeek(segment.start_seconds)}
      aria-current={active ? "true" : undefined}
      className={cn(
        "block w-full rounded px-1 py-0.5 text-left hover:bg-surface-hover",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-8",
        active && "bg-accent-3",
      )}
      title="Play from here"
    >
      {body}
    </button>
  );
}
